import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { CopyField } from "../components/ui/CopyField";
import { ErrorText } from "../components/ui/feedback";
import { Field } from "../components/ui/Field";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { inviteLinkFromApiUrl } from "../features/members/queries";
import { adminApi } from "./api";

export interface InviteSoundGuyModalProps {
  onClose: () => void;
  /** Prefills from an invite request. The admin can still change both fields. */
  initialEmail?: string;
  initialWorkspaceName?: string;
  /** Fires once the invite exists, so the caller can mark the request as invited. */
  onCreated?: () => void;
}

/**
 * Creates an invite for a new operator. The invite URL is returned once and shown in this
 * dialog only; the page never lists it. While the URL is visible the dialog cannot be
 * dismissed by Escape, so it is not lost by accident.
 */
export function InviteSoundGuyModal({
  onClose,
  initialEmail = "",
  initialWorkspaceName = "",
  onCreated,
}: InviteSoundGuyModalProps) {
  const { t } = useI18n();
  const [email, setEmail] = useState(initialEmail);
  const [workspaceName, setWorkspaceName] = useState(initialWorkspaceName);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => adminApi.createInvite(email.trim(), workspaceName.trim() || undefined),
    onSuccess: (result) => {
      setInviteUrl(inviteLinkFromApiUrl(result.url, window.location.origin));
      onCreated?.();
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (email.trim()) create.mutate();
  };

  if (inviteUrl) {
    return (
      <Modal title={t("admin.invite.title")} onClose={onClose} dismissible={false}>
        <div className="space-y-4">
          <p role="note" className="text-note text-ink-2">
            {t("admin.invite.shownOnce")}
          </p>
          <CopyField value={inviteUrl} />
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>
              {t("admin.invite.done")}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t("admin.invite.title")} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("admin.invite.email")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("admin.invite.workspaceName")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={workspaceName}
              onChange={(event) => setWorkspaceName(event.target.value)}
            />
          )}
        </Field>
        <ErrorText error={create.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={create.isPending}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            {t("admin.invite.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
